import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { GroupAnalyticsPanel } from '../components/AccountAnalytics';

function formatDisplayDate(value) {
  if (!value) return 'Never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDisplayDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatMilestoneTiers(milestones = []) {
  if (!Array.isArray(milestones) || milestones.length === 0) return '—';
  return milestones.map((m) => `${m}x`).join(' · ');
}

function formatRecoveryTiers(milestones = []) {
  if (!Array.isArray(milestones) || milestones.length === 0) return '—';
  return milestones.map((m) => `+${m}%`).join(' · ');
}

function formatRelativeTime(value) {
  if (!value) return 'Never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Never';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  return formatDisplayDate(value);
}

function ScanActivityBadge({ activity, tokensTracked }) {
  if (!tokensTracked) {
    return (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
        No tokens
      </span>
    );
  }
  const map = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    idle: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    inactive: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    none: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  };
  const label = activity === 'active' ? 'Scanning' : activity === 'idle' ? 'Idle' : activity === 'inactive' ? 'Inactive' : 'No tokens';
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${map[activity] || map.none}`}>
      {label}
    </span>
  );
}

function formatLimitPercent(value, noneLabel = 'No limit') {
  if (value == null || value === 0) return noneLabel;
  return `${value}%`;
}

function formatMarketCap(value) {
  if (value == null) return '—';
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${Math.round(value / 1e3)}K`;
  return `$${value}`;
}

function displayAlertTier(group) {
  return group.effectiveAlertTier || (group.membershipTier === 'free' ? 'starter' : group.membershipTier);
}

function getTierBadge(tier) {
  const t = tier === 'enterprise' ? 'premium' : (tier === 'free' ? 'starter' : tier);
  // Soft tags — no loud Alpha/Premium pills in the list
  const badges = {
    starter: 'text-gray-400 dark:text-gray-500',
    alpha: 'text-amber-600/80 dark:text-amber-400/80',
    premium: 'text-blue-600/80 dark:text-blue-400/80',
  };
  return badges[t] || badges.starter;
}

function formatTierLabel(tier) {
  const labels = { starter: 'Starter', free: 'Starter', alpha: 'α', premium: 'Pro', enterprise: 'Pro' };
  return labels[tier] || (tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : 'Starter');
}

function ToggleBadge({ enabled, onLabel = 'ON', offLabel = 'OFF', onClick, disabled = false, loading = false }) {
  const baseClass = enabled
    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/60'
    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600';

  if (!onClick) {
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${baseClass}`}>
        {enabled ? onLabel : offLabel}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      title={enabled ? 'Click to turn off' : 'Click to turn on'}
      className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full transition-colors disabled:opacity-50 ${baseClass}`}
    >
      {loading ? '…' : enabled ? onLabel : offLabel}
    </button>
  );
}

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [filterScan, setFilterScan] = useState('all');
  const [togglingCommand, setTogglingCommand] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    fetchGroups();
  }, [filterScan]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get('/groups/analytics');
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch group analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const params = filterScan !== 'all' ? { scanActivity: filterScan } : {};
      const response = await api.get('/groups', { params });
      setGroups(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setError('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const openLeaderboardRow = (row) => {
    if (row?.id) viewGroupDetails(row.id);
  };

  const viewGroupDetails = async (groupId) => {
    try {
      const response = await api.get(`/groups/${groupId}`);
      setSelectedGroup(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Failed to fetch group details:', error);
      alert('Failed to load group details');
    }
  };

  const updateGroupTier = async (groupId, newTier) => {
    try {
      const payload = { membershipTier: newTier };
      if (newTier === 'premium') payload.durationDays = 30;
      await api.put(`/groups/${groupId}`, payload);
      alert('Group tier updated successfully');
      fetchGroups();
      if (selectedGroup && selectedGroup._id === groupId) {
        viewGroupDetails(groupId);
      }
    } catch (error) {
      console.error('Failed to update group tier:', error);
      alert('Failed to update group tier');
    }
  };

  const formatSubscription = (entity) => {
    const tier = entity.membershipTier === 'enterprise' ? 'premium' : entity.membershipTier;
    if (tier !== 'premium') return '—';
    const start = entity.membershipStartDate
      ? new Date(entity.membershipStartDate).toLocaleDateString()
      : '—';
    const expiry = entity.membershipExpiryDate
      ? new Date(entity.membershipExpiryDate).toLocaleDateString()
      : '—';
    return `${start} → ${expiry}`;
  };

  const toggleGroupStatus = async (groupId, currentStatus) => {
    try {
      await api.put(`/groups/${groupId}`, { isActive: !currentStatus });
      alert(`Group ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchGroups();
      if (selectedGroup && selectedGroup._id === groupId) {
        viewGroupDetails(groupId);
      }
    } catch (error) {
      console.error('Failed to update group status:', error);
      alert('Failed to update group status');
    }
  };

  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.telegramId?.toString().includes(searchTerm);
    const groupTier = displayAlertTier(group);
    const matchesTier = filterTier === 'all' || groupTier === filterTier || (filterTier === 'starter' && groupTier === 'free');
    return matchesSearch && matchesTier;
  });

  const toggleMute = async (groupId, group, e) => {
    e.stopPropagation();
    const muted = !group.alertsMuted;
    try {
      await api.put(`/groups/${groupId}/mute`, { muted });
      setGroups(prev => prev.map(g => g._id === groupId ? { ...g, alertsMuted: muted } : g));
      if (selectedGroup && selectedGroup._id === groupId) {
        setSelectedGroup(prev => ({ ...prev, alertsMuted: muted }));
      }
    } catch (err) {
      console.error('Failed to toggle mute', err);
      alert('Failed to update mute status');
    }
  };

  const toggleCommand = async (groupId, key, enabled) => {
    setTogglingCommand(key);
    try {
      const response = await api.put(`/groups/${groupId}/commands`, { key, enabled: !enabled });
      const updated = response.data;
      setGroups(prev => prev.map(g => (g._id === groupId ? { ...g, ...updated } : g)));
      if (selectedGroup && selectedGroup._id === groupId) {
        setSelectedGroup(updated);
      }
    } catch (err) {
      console.error('Failed to toggle command', err);
      alert('Failed to update command setting');
    } finally {
      setTogglingCommand(null);
    }
  };

  const upgradeToAlpha = async (groupId) => {
    if (!window.confirm('Upgrade this group to Alpha? They will have free membership forever with no cook credits required.')) return;
    try {
      await api.put(`/groups/${groupId}`, { membershipTier: 'alpha' });
      alert('Group upgraded to Alpha successfully');
      fetchGroups();
      if (selectedGroup && selectedGroup._id === groupId) {
        viewGroupDetails(groupId);
      }
    } catch (error) {
      console.error('Failed to upgrade group to Alpha:', error);
      alert('Failed to upgrade group to Alpha');
    }
  };

  const isPaidGroupTier = (tier) => {
    const t = tier === 'enterprise' ? 'premium' : (tier === 'free' ? 'starter' : tier);
    return t === 'premium' || t === 'alpha';
  };

  const downgradeGroupToStarter = async (groupId) => {
    if (!window.confirm('Downgrade this group to Starter? Premium/Alpha access will be removed immediately.')) return;
    await updateGroupTier(groupId, 'starter');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl">Loading groups...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Groups</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Leaderboards, premium/alpha performance, and scan activity
          </p>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Total: {groups.length} groups
        </div>
      </div>

      <GroupAnalyticsPanel
        analytics={analytics}
        loading={analyticsLoading}
        onSelectRow={openLeaderboardRow}
      />

      <div className="mb-6 flex flex-wrap gap-4">
        <input
          type="text"
          placeholder="Search by group name or Telegram ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 min-w-[200px] px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
        />
        <select
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
        >
          <option value="all">All Tiers</option>
          <option value="starter">Starter</option>
          <option value="alpha">Alpha</option>
          <option value="premium">Premium</option>
        </select>
        <select
          value={filterScan}
          onChange={(e) => setFilterScan(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
        >
          <option value="all">All scan activity</option>
          <option value="active">Scanning (&lt;24h)</option>
          <option value="idle">Idle (1–7d)</option>
          <option value="inactive">Inactive (&gt;7d)</option>
          <option value="none">No tokens</option>
        </select>
      </div>

      {/* Groups Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Group
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Telegram ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Alert tier
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Tokens
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Last scan
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Scan status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                CK Scan
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Alerts
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredGroups.map((group) => (
              <tr
                key={group._id}
                className="hover:bg-gray-50 dark:bg-gray-800/50 cursor-pointer"
                onClick={() => viewGroupDetails(group._id)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                      {group.title?.[0]?.toUpperCase() || 'G'}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {group.title || 'Unknown Group'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {group.type === 'supergroup' ? 'Supergroup' : 'Group'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {group.telegramId}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-0.5">
                    <span className={`text-[11px] font-medium ${getTierBadge(displayAlertTier(group))}`}>
                      {formatTierLabel(displayAlertTier(group))}
                    </span>
                    {group.subscriptionExpired ? (
                      <span className="text-[10px] text-gray-400">expired</span>
                    ) : null}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {formatSubscription(group)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-mono">
                  {group.tokensTracked ?? group.totalTokensTracked ?? 0}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  <div>{formatRelativeTime(group.lastScanAt)}</div>
                  {group.lastScanAt ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400">{formatDisplayDateTime(group.lastScanAt)}</div>
                  ) : null}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ScanActivityBadge activity={group.scanActivity} tokensTracked={group.tokensTracked ?? 0} />
                  {group.botRemoved ? (
                    <div className="text-[10px] text-red-500 mt-1">Bot removed</div>
                  ) : !group.isActive ? (
                    <div className="text-[10px] text-gray-500 mt-1">Account inactive</div>
                  ) : null}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <ToggleBadge
                    enabled={group.scanEnabled}
                    onLabel="ON"
                    offLabel="OFF"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCommand(group._id, 'scan', group.scanEnabled);
                    }}
                    loading={togglingCommand === 'scan'}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => toggleMute(group._id, group, e)}
                    title={group.alertsMuted ? 'Unmute: group will receive milestone & recovery alerts' : 'Mute: stop milestone & recovery alerts for this group'}
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      group.alertsMuted
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    } hover:opacity-90`}
                  >
                    {group.alertsMuted ? '🔇 Muted' : '🔔 Alerts on'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      viewGroupDetails(group._id);
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Group Detail Modal */}
      {showModal && selectedGroup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Group Details</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {/* Basic Info */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Group Name</p>
                      <p className="font-medium">{selectedGroup.title || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Telegram ID</p>
                      <p className="font-medium">{selectedGroup.telegramId}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Type</p>
                      <p className="font-medium">{selectedGroup.type || 'group'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Members</p>
                      <p className="font-medium">{selectedGroup.memberCount || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Created</p>
                      <p className="font-medium">{formatDisplayDate(selectedGroup.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last Active</p>
                      <p className="font-medium">{formatDisplayDate(selectedGroup.lastActiveAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Membership */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">Membership &amp; alerts</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Alert tier (effective)</p>
                      <span className={`text-sm font-medium ${getTierBadge(displayAlertTier(selectedGroup))}`}>
                        {({ starter: 'Starter', free: 'Starter', alpha: 'Alpha', premium: 'Premium', enterprise: 'Premium' })[displayAlertTier(selectedGroup)] || 'Starter'}
                      </span>
                      {selectedGroup.subscriptionExpired ? (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Premium subscription ended — alerting uses Starter limits.</p>
                      ) : null}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Stored tier</p>
                      <p className="font-medium">{formatTierLabel(selectedGroup.membershipTier)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Subscription</p>
                      <p className="font-medium">{formatSubscription(selectedGroup)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Account status</p>
                      <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                        selectedGroup.isActive ? 'bg-green-100 text-green-800 dark:text-green-300' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedGroup.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Background scanning */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">Background scanning</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Price polling continues for all tracked tokens regardless of subscription. Tier only affects alert delivery and limits.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last scan</p>
                      <p className="font-medium">{formatRelativeTime(selectedGroup.lastScanAt)}</p>
                      {selectedGroup.lastScanAt ? (
                        <p className="text-xs text-gray-500">{formatDisplayDateTime(selectedGroup.lastScanAt)}</p>
                      ) : null}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Scan status</p>
                      <ScanActivityBadge activity={selectedGroup.scanActivity} tokensTracked={selectedGroup.tokensTracked ?? 0} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Tokens tracked</p>
                      <p className="font-medium font-mono">{selectedGroup.tokensTracked ?? selectedGroup.totalTokensTracked ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last alert sent</p>
                      <p className="font-medium">{formatDisplayDateTime(selectedGroup.lastAlertAt) || 'Never'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last chat activity</p>
                      <p className="font-medium">{formatDisplayDate(selectedGroup.lastActiveAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Members</p>
                      <p className="font-medium">{selectedGroup.memberCount || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">Usage Statistics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Tokens Tracked</p>
                      <p className="font-medium">{selectedGroup.stats?.tokensTracked || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Alerts Sent</p>
                      <p className="font-medium">{selectedGroup.stats?.alertsSent || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Owner ID</p>
                      <p className="font-medium">{selectedGroup.ownerId || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Commands Status */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">Commands Status</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Click ON/OFF to toggle commands for this group (admin override).
                  </p>
                  <div className="space-y-2">
                    {(selectedGroup.commandStatus || []).map((cmd) => (
                      <div
                        key={cmd.key}
                        className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            <span className="font-mono text-blue-600 dark:text-blue-400">{cmd.command}</span>
                            {' · '}
                            {cmd.label}
                            {cmd.pro && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide align-middle">
                                Pro
                              </span>
                            )}
                          </p>
                          {cmd.note && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{cmd.note}</p>
                          )}
                          {cmd.lastAt && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Last sent: {formatDisplayDateTime(cmd.lastAt)}
                            </p>
                          )}
                        </div>
                        <ToggleBadge
                          enabled={cmd.enabled}
                          onClick={() => toggleCommand(selectedGroup._id, cmd.key, cmd.enabled)}
                          loading={togglingCommand === cmd.key}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Milestone & recovery settings (effective for this group) */}
                {selectedGroup.alertSettings && (
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-1">Milestone & Recovery Settings</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      {selectedGroup.alertSettings.label || 'Global group defaults'} — applies to this group unless muted above.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">🎯 Milestones</h4>
                          <ToggleBadge
                            enabled={selectedGroup.alertSettings.enableMilestoneAlerts !== false}
                            onLabel="Enabled"
                            offLabel="Disabled"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Alert tiers</p>
                          <p className="text-sm font-medium font-mono text-gray-900 dark:text-white">
                            {formatMilestoneTiers(selectedGroup.alertSettings.milestones)}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Max milestone</p>
                            <p className="text-sm font-medium">
                              {formatLimitPercent(selectedGroup.alertSettings.maxMilestonePercent)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Cooldown</p>
                            <p className="text-sm font-medium">
                              {selectedGroup.alertSettings.milestoneCooldownMinutes ?? 60} min
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">📈 Recovery</h4>
                          <ToggleBadge
                            enabled={selectedGroup.alertSettings.enableRecoveryAlerts !== false}
                            onLabel="Enabled"
                            offLabel="Disabled"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Recovery milestones</p>
                          <p className="text-sm font-medium font-mono text-gray-900 dark:text-white">
                            {formatRecoveryTiers(selectedGroup.alertSettings.recoveryMilestones)}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Min drawdown from ATH</p>
                            <p className="text-sm font-medium">
                              {selectedGroup.alertSettings.minDrawdownPercent ?? 70}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Min recovery to alert</p>
                            <p className="text-sm font-medium">
                              +{selectedGroup.alertSettings.minRecoveryPercent ?? 150}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Min market cap</p>
                            <p className="text-sm font-medium">
                              {formatMarketCap(selectedGroup.alertSettings.minMarketCapForRecovery ?? 5000)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Cooldown</p>
                            <p className="text-sm font-medium">
                              {selectedGroup.alertSettings.recoveryCooldownMinutes ?? 60} min
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Max recovery alert</p>
                          <p className="text-sm font-medium">
                            {formatLimitPercent(selectedGroup.alertSettings.maxRecoveryPercent)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                      Global defaults: Settings → Alert Settings. Group admins can override in Telegram with <code className="font-mono">ck ms set …</code> and <code className="font-mono">ck rc set …</code>.
                    </p>
                  </div>
                )}

                {/* Alerts muted status */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">Alerts</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {selectedGroup.alertsMuted
                      ? 'This group is muted — no milestone or recovery alerts are sent here.'
                      : 'This group receives milestone and recovery alerts.'}
                  </p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMute(selectedGroup._id, selectedGroup, e);
                    }}
                    className={`px-4 py-2 rounded ${
                      selectedGroup.alertsMuted
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-amber-600 hover:bg-amber-700'
                    } text-white`}
                  >
                    {selectedGroup.alertsMuted ? 'Unmute alerts' : 'Mute alerts'}
                  </button>
                </div>

                {/* Actions */}
                <div className="pt-4">
                  <h3 className="text-lg font-semibold mb-3">Actions</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => upgradeToAlpha(selectedGroup._id)}
                      className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700"
                    >
                      Upgrade to Alpha
                    </button>
                    <button
                      onClick={() => updateGroupTier(selectedGroup._id, 'premium')}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Upgrade to Premium (30 days)
                    </button>
                    {isPaidGroupTier(selectedGroup.membershipTier) && (
                      <button
                        onClick={() => downgradeGroupToStarter(selectedGroup._id)}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                      >
                        Downgrade to Starter
                      </button>
                    )}
                    <button
                      onClick={() => toggleGroupStatus(selectedGroup._id, selectedGroup.isActive)}
                      className={`px-4 py-2 rounded ${
                        selectedGroup.isActive
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-green-600 hover:bg-green-700'
                      } text-white`}
                    >
                      {selectedGroup.isActive ? 'Deactivate' : 'Activate'} Group
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
